import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';

import cls from 'classnames/bind';

import getDaysInMonth from 'date-fns/getDaysInMonth';
import get from 'lodash/get';
import noop from 'lodash/noop';
import throttle from 'lodash/throttle';

import SelectArrowIcon from '@ott/icon-select-arrow';
import LoadingDots from '@ott/loading-dots';
import Money from '@ott/money';
import declOfNum from '@ott/utility-decl-of-num';

import PriceDate from '../PriceDate/PriceDate';

import styles from './PriceChart.scss';

const cx = cls.bind(styles);

const getIntlMonthName = Intl && Intl.DateTimeFormat('ru', { month: 'long' }).format;
const getMonthName = (date) => {
  if (getIntlMonthName) {
    const name = getIntlMonthName(date);

    return name[0].toUpperCase() + name.slice(1);
  }

  return '';
};

const now = new Date();
const addYearIfNeeded = (date) => {
  const fullYear = date.getFullYear();

  if (fullYear > now.getFullYear() && date.getMonth() === 0) {
    return `, ${fullYear}`;
  }

  return '';
};

class PriceChart extends PureComponent {
  static propTypes = {
    prices: PropTypes.objectOf(PropTypes.oneOfType(PropTypes.object)),
    statuses: PropTypes.objectOf(PropTypes.oneOfType(PropTypes.object)),
    nights: PropTypes.number,
    adults: PropTypes.number,
    prepareSearchParams: PropTypes.func,
    onChartScroll: PropTypes.func,
    onDateClick: PropTypes.func,
  };

  static defaultProps = {
    prices: {},
    statuses: {},
    prepareSearchParams: noop,
    onChartScroll: noop,
    onDateClick: noop,
  };

  constructor(props, ctx) {
    super(props, ctx);

    this.state = {
      hideLeftControl: true,
      hideRightControl: false,
    };
  }

  renderMonth(month, i) {
    const {
      prices: { minPrice, maxPrice },
      prepareSearchParams,
      onDateClick,
      routeFromOtherApp,
    } = this.props;

    let firstDate = get(month, 'dates[0][0]');

    // due to SSR state date to string conversion
    if (!(firstDate instanceof Date)) {
      firstDate = new Date(firstDate);
    }

    let showMonth = true;
    let showYear = true;
    if (i === 0) {
      const daysLeftInMonth = getDaysInMonth(firstDate) - firstDate.getDate() + 1;
      showMonth = daysLeftInMonth > 4;
      showYear = daysLeftInMonth > 7;
    }

    return (
      <div className={cx('month')} key={i}>
        <div className={cx('month-dates')}>
          {month.dates.map(([date, price], index) => {
            // due to SSR state date to string conversion
            if (!(date instanceof Date)) {
              // eslint-disable-next-line no-param-reassign
              date = new Date(date);
            }

            return (
              <PriceDate
                date={date}
                price={price}
                minPrice={minPrice}
                maxPrice={maxPrice}
                prepareSearchParams={prepareSearchParams}
                routeFromOtherApp={routeFromOtherApp}
                onDateClick={onDateClick}
                // eslint-disable-next-line react/no-array-index-key
                key={index}
              />
            );
          })}
        </div>

        {showMonth && (
          <div className={cx('month-name')}>
            {showMonth && getMonthName(firstDate)}
            {showYear && addYearIfNeeded(firstDate)}
          </div>
        )}
      </div>
    );
  }

  renderMarks() {
    const { prices } = this.props;

    const { minPrice, maxPrice } = prices;

    if (maxPrice === 0) {
      return null;
    }

    return (
      <div className={cx('marks')}>
        <div className={cx('mark', 'mark-top')}>
          <div className={cx('mark-price')}>
            <Money amount={maxPrice} />
          </div>
        </div>
        <div className={cx('mark', 'mark-middle')}>
          <div className={cx('mark-price')}>
            <Money amount={(maxPrice + minPrice) / 2} />
          </div>
        </div>
      </div>
    );
  }

  // todo add fallback
  handleScrollByControl(direction) {
    const scrollerNode = this.scrollerRef;
    if (scrollerNode && scrollerNode.scrollBy) {
      scrollerNode.scrollBy({
        left: 350 * (direction === 'left' ? -1 : 1),
        behavior: 'smooth',
      });
    }
  }

  handleLeftControlClick = () => this.handleScrollByControl('left');

  handleRightControlClick = () => this.handleScrollByControl('right');

  renderControl(side) {
    const { hideLeftControl, hideRightControl } = this.state;

    if (side === 'left' && hideLeftControl) {
      return null;
    }
    if (side === 'right' && hideRightControl) {
      return null;
    }

    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
      <div
        className={cx('control', `control-${side}`)}
        onClick={side === 'left' ? this.handleLeftControlClick : this.handleRightControlClick}
      >
        <SelectArrowIcon
          w={15}
          style={{ transform: `rotate(${side === 'right' ? '-' : ''}90deg)` }}
        />
      </div>
    );
  }

  handleScrollerScroll = () => {
    const { onChartScroll } = this.props;

    const scrollerNode = this.scrollerRef;
    if (scrollerNode) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollerNode;

      if (scrollLeft === 0) {
        this.setState({
          hideLeftControl: true,
        });
      } else if (
        // доскроллено до правой границы
        scrollLeft ===
        scrollWidth - clientWidth
      ) {
        this.setState({
          hideRightControl: true,
        });
      } else {
        this.setState({
          hideLeftControl: false,
          hideRightControl: false,
        });
      }
    }

    onChartScroll();
  };

  handleScrollerScrollThrottled = throttle(this.handleScrollerScroll, 200);

  // eslint-disable-next-line no-return-assign
  setScrollerRef = (ref) => (this.scrollerRef = ref);

  renderChart() {
    const {
      prices,
      statuses: { loading, success, error },
    } = this.props;

    return (
      <div className={cx('chart')}>
        <div className={cx('scroller-container')}>
          {success && this.renderMarks()}

          <div
            className={cx('scroller')}
            ref={this.setScrollerRef}
            onScroll={this.handleScrollerScrollThrottled}
          >
            {prices.years.map((year, i) => {
              return (
                // eslint-disable-next-line react/no-array-index-key
                <div className={cx('year')} key={i}>
                  <div>
                    {year.months.map((month, index) => {
                      return this.renderMonth(month, index);
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {this.renderControl('left')}
        {this.renderControl('right')}

        {loading && (
          <div className={cx('loader')}>
            <LoadingDots />
          </div>
        )}
        {error && <div className={cx('notFound')}>Ничего не найдено</div>}
      </div>
    );
  }

  renderSearchParams() {
    const { nights, adults } = this.props;

    return (
      <div className={cx('searchParams')}>
        Цены рассчитаны на {nights} {declOfNum(nights, ['ночь', 'ночи', 'ночей'])} / {adults}{' '}
        {declOfNum(adults, ['человек', 'человека', 'человек'])}
      </div>
    );
  }

  render() {
    return (
      <div className={cx('PriceChart')}>
        <div className={cx('inner')}>
          {this.renderSearchParams()}
          {this.renderChart()}
        </div>
      </div>
    );
  }
}

export default PriceChart;
